import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

import Logo from "../../assets/logo.png";

import LegendTable from "../../components/Common/LegendTable";
import TableHeader from "../../components/Common/TableHeader";
import InfoCard from "../../components/Common/InfoCard";

import { fetchOccupancyInsights } from "../../store/thunks/analyticsThunks";
import { formatDateToISO, formatMonthToDateRange } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";
import showToast from "../../utils/toastNotifications";
import { load } from "@amcharts/amcharts4/.internal/core/utils/Net";

import { monthArray } from "../../constants/commonConstants";

const PAYMENT_COLORS = {
  Total: "#B488ED",
  Late: "#FF6666",
  Ontime: "#9BDFB5",
  Advance: "#8DA7F6",
};

const PaymentDistribution = () => {
  const dispatch = useDispatch();
  const { data, isLoading } = useSelector((state) => state.analytics || {});
  const rawData = Array.isArray(data?.propertyData) ? data.propertyData : [];
  const monthlyTrendData = Array.isArray(data?.monthlyTrendData) ? data.monthlyTrendData : [];
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    propertyId: "",
    startDate: "",
    endDate: "",
    month: "",
  });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const paymentChartRef = useRef(null);
  const paymentLegendRef = useRef(null);
  const trendChartRef = useRef(null);
  const trendLegendRef = useRef(null);


  const fetchData = useCallback(() => {
    let month;
    if(!filters.month || filters.month === '') {
      month = new Date().toLocaleString('default', { month: 'long' });
    } else {
      month = filters.month;
    }
    const {startDate, endDate} = formatMonthToDateRange(month);
    const query = {
      propertyId: filters.propertyId,
      startDate: formatDateToISO(startDate, false),
      endDate: formatDateToISO(endDate, true),
      month: month,
    };
    dispatch(fetchOccupancyInsights(query));
  }, [dispatch, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const propertyName = searchParams.get("propertyName") || "";
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const month = searchParams.get("month");

    const propertyId = rawData.find(
      (property) => property.propertyName === propertyName
    )?._id;

    setFilters({
      propertyId: propertyId || "",
      startDate: startDate && startDate !== "null" ? startDate : "",
      endDate: endDate && endDate !== "null" ? endDate : "",
      month: month || "",
    });
  }, [searchParams]);

  const handleSearch = () => {
    const queryParams = new URLSearchParams();

    const selectedProperty = rawData.find(
      (property) => property._id === filters.propertyId
    );

    queryParams.set("propertyName", selectedProperty ? selectedProperty.propertyName : "");
   // queryParams.set("propertyId", filters.propertyId);

    queryParams.set("startDate", filters.startDate);
    queryParams.set("endDate", filters.endDate);
    queryParams.set("page", 1);
    queryParams.set("month", filters.month);

    setSearchParams(queryParams);

    fetchData();
  };

  const uniqueProperties = useMemo(() => {
    
    if (!rawData.length) {
      return [
        {
          label: "🚫 No property",
          value: "none",
          disabled: true,
        },
      ];
    }

    return rawData.map((item) => ({
      label: item.propertyName,
      value: item._id,
    }));
  }, [rawData]);

  const selectedProperty = rawData.find((p) => p._id === filters.propertyId);
  const selectedPropertyName = selectedProperty
    ? selectedProperty.propertyName
    : "All properties";

  const filteredData = useMemo(() => {
    return filters.propertyId
      ? rawData.filter((item) => item._id === filters.propertyId)
      : rawData;
  }, [rawData, filters.propertyId]);

  const paymentBreakdown = useMemo(() => {
    let totals = {
      Total: 0,
      Late: 0,
      Ontime: 0,
      Advance: 0,
    };

    filteredData.forEach((item) => {
      totals.Total += parseFloat(item.rentCollect || 0);
      totals.Late += parseFloat(item.lateRentPaid || 0);
      totals.Ontime += parseFloat(item.ontimeRentPaid || 0);
      totals.Advance += parseFloat(item.advanceRentPaid || 0);
    });

    const totalSum = totals.Total || 1;

    return Object.entries(totals).map(([label, value]) => ({
      label,
      value: value.toLocaleString(),
      percent: (value / totalSum) * 100,
      color: PAYMENT_COLORS[label],
    }));
  }, [filteredData]);

  // removed total for rent chart
  const rentChartPayment = useMemo(() => 
    paymentBreakdown.filter(item => item.label !== "Total"), 
    [paymentBreakdown]
  );

  const rentChartSeries = useMemo(
    () => [
      {
        name: "Tenant Payment Status",
        data: rentChartPayment.map((item) =>
          parseFloat(item.percent.toFixed(2))
        ),
      },
    ],
    [rentChartPayment]
  );

  const rentChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: 400,
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          horizontal: false,
          columnWidth: "40%",
          distributed: true,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: "16px",
          fontWeight: "bold",
          colors: rentChartPayment.map((item) => item.color),
        },
        formatter: (val) => `${val.toFixed(2)}%`,
      },
      xaxis: {
        categories: rentChartPayment.map((item) => item.label),
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: ["#00000099", "#00000099", "#00000099", "#00000099"],
          },
        },
      },
      yaxis: {
        title: {
          text: "",
        },
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val.toFixed(0)}`,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val.toFixed(2)}%`,
        },
      },
      colors: rentChartPayment.map((item) => item.color),
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.25,
          opacityFrom: 1,
          opacityTo: 0.25,
          stops: [0, 100],
        },
      },
    }),
    [rentChartPayment]
  );

  const monthlyTrendChartSeries = useMemo(() => {
    const total =
      monthlyTrendData.reduce((sum, item) => {
        const val = parseFloat(item.value || 0);
        return !isNaN(val) ? sum + val : sum;
      }, 0) || 1;

    return [
      {
        name: "Income",
        data: monthlyTrendData.map((item) => {
          const val = parseFloat(item.value || 0);
          const percent = total > 0 ? (val / total) * 100 : 0;
          return parseFloat(percent.toFixed(2));
        }),
      },
    ];
  }, [monthlyTrendData]);

  const monthlyTrendChartOptions = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 400,
        toolbar: { show: false },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          gradientToColors: ["rgba(146, 80, 229, 0.25)"],
          stops: [0, 100],
          opacityFrom: 1,
          opacityTo: 1,
        },
      },
      xaxis: {
        categories: monthlyTrendData.map((item) => item.label),
        title: {
          text: "Months",
          style: { fontSize: "14px", fontWeight: 600 },
        },
        labels: {
          style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => `${val.toFixed(0)}`,
          style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        },
        title: {
          text: "Income",
          style: { fontSize: "14px", fontWeight: 600 },
        },
      },
      tooltip: {
        y: { formatter: (val) => `${val.toFixed(2)}%` },
      },
      colors: ["#9250E5"],
    }),
    [monthlyTrendData]
  );

  const createInfoCardSVG = (data) => {
    if (!data || data.length === 0) {
      return `
        <svg width="800" height="200" xmlns="http://www.w3.org/2000/svg">
          <text x="400" y="100" font-family="Inter, sans-serif" font-size="16" text-anchor="middle" fill="#666">
            No payment data available
          </text>
        </svg>
      `;
    }

    const totalSvgWidth = 800;
    const cardHeight = 100;
    const startY = 40;
    const padding = 20;
    const cardSpacing = 15;
    
    // Calculate width weights based on value length
    const widthWeights = data.map(item => {
      const valueLength = item.value.toString().length;
      // Give more weight to longer values  
      if (valueLength > 17) return 2;    
      return 1;                            
    });
    
    const totalWeight = widthWeights.reduce((sum, weight) => sum + weight, 0);
    const availableWidth = totalSvgWidth - (2 * padding) - ((data.length - 1) * cardSpacing);
    
    // Calculate individual card widths based on weights
    const cardWidths = widthWeights.map(weight => 
      Math.floor((weight / totalWeight) * availableWidth)
    );
    
    // Ensure minimum width for readability
    const minCardWidth = 120;
    const adjustedCardWidths = cardWidths.map(width => Math.max(width, minCardWidth));
    
    // Generate cards dynamically with variable widths
    const cardsContent = data.map((item, index) => {
      // Calculate x position based on previous card widths
      const xPosition = padding + adjustedCardWidths.slice(0, index).reduce((sum, width) => sum + width + cardSpacing, 0);
      const cardWidth = adjustedCardWidths[index];
      
      // Calculate text positioning within each card
      const textXStart = xPosition + 10;
      const labelY = startY + 25;
      const valueY = startY + 60;
      
      // Keep consistent font size - no more dynamic sizing!
      const fontSize = 18; // Fixed font size for consistency
      
      // Check if text will fit, if not we can use text wrapping
      const valueLength = item.value.toString().length;
      const estimatedTextWidth = valueLength * 8; // Rough estimate: 8px per character
      const availableTextWidth = cardWidth - 20; // Account for padding
      
      let valueText = item.value;
      let secondLineY = null;
      
      // If text is too long, split it for wrapping
      if (estimatedTextWidth > availableTextWidth && valueLength > 10) {
        const midPoint = Math.floor(valueLength / 2);
        const commaIndex = item.value.indexOf(',', midPoint);
        
        if (commaIndex !== -1 && commaIndex < valueLength - 3) {
          const firstPart = item.value.substring(0, commaIndex + 1);
          const secondPart = item.value.substring(commaIndex + 1);
          valueText = firstPart;
          secondLineY = valueY + 15;
          
          return `
            <!-- Card ${index + 1}: ${item.label} -->
            <rect x="${xPosition}" y="${startY}" width="${cardWidth}" height="${cardHeight}" rx="5" fill="white" filter="url(#shadow)"/>
            
            <!-- Label -->
            <text x="${textXStart}" y="${labelY}" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
              ${item.label} (UGX)
            </text>
            
            <!-- Value - First Line -->
            <text x="${textXStart}" y="${valueY - 5}" font-family="Inter, sans-serif" font-size="${fontSize}" font-weight="bold" fill="url(#textGradient)">
              ${firstPart}
            </text>
            
            <!-- Value - Second Line -->
            <text x="${textXStart}" y="${secondLineY}" font-family="Inter, sans-serif" font-size="${fontSize}" font-weight="bold" fill="url(#textGradient)">
              ${secondPart}
            </text>
          `;
        }
      }
      
      return `
        <!-- Card ${index + 1}: ${item.label} -->
        <rect x="${xPosition}" y="${startY}" width="${cardWidth}" height="${cardHeight}" rx="5" fill="white" filter="url(#shadow)"/>
        
        <!-- Label -->
        <text x="${textXStart}" y="${labelY}" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          ${item.label} (UGX)
        </text>
        
        <!-- Value with consistent font size -->
        <text x="${textXStart}" y="${valueY}" font-family="Inter, sans-serif" font-size="${fontSize}" font-weight="bold" fill="url(#textGradient)">
          ${valueText}
        </text>
      `;
    }).join('');

    // Calculate actual total width used
    const totalUsedWidth = padding + adjustedCardWidths.reduce((sum, width) => sum + width, 0) + ((data.length - 1) * cardSpacing) + padding;
    const finalSvgWidth = Math.min(totalUsedWidth, totalSvgWidth);

    const svgContent = `
      <svg width="${finalSvgWidth}" height="200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
          </linearGradient>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.1)"/>
          </filter>
        </defs>
        
        ${cardsContent}
      </svg>
    `;
    
    return svgContent;
  };

  const downloadPdf = async () => {
    try {

      let month;
      if(!filters.month || filters.month === '') {
        month = new Date().toLocaleString('default', { month: 'long' });
      } else {
        month = filters.month;
      }
      const {startDate, endDate} = formatMonthToDateRange(month);

      let dateRangeText = `${startDate} to ${endDate}`;
      let propertyName = selectedPropertyName;

      if (filteredData.length === 0) {
        showToast("info", "No data available for the selected filters.");
        setIsGeneratingPdf(false);
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');

      let pageHeight = pdf.internal.pageSize.height;
      let pageWidth = pdf.internal.pageSize.width;

      let yPosition = 20;
      let xPosition = 20;
      
      // Contact fields 
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      
      pdf.text('Address:', xPosition, yPosition);
      pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);

      pdf.text('Contact:', xPosition, yPosition + 5);
      pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);

      pdf.text('Email:', xPosition, yPosition + 10);
      pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);

      yPosition += 20;

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Payment Distribution Report', 20, yPosition);

      yPosition += 10;

      // Report metadata
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      pdf.text('Reporting Period:', 20, yPosition);

      pdf.setTextColor(0, 0, 0);
      pdf.text(dateRangeText, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Property:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(propertyName, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Generated on:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const now = new Date();
      const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
      pdf.text(generationTime, 50, yPosition);
      yPosition += 15;

      // Applied filters section
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Applied Filters', 20, yPosition);
      yPosition += 8;

      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      
      if (propertyName) {
        pdf.text(`• Property Filter: ${propertyName}`, 25, yPosition);
        yPosition += 5;
      }
      
      pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
      yPosition += 10;

      // Create SVG InfoCard with gradients
      const paymentInfoCard = createInfoCardSVG(paymentBreakdown);

      const paymentInfoCardCanvas = await svgToCanvas(paymentInfoCard, 800, 200); 
      const paymentInfoCardImgData = paymentInfoCardCanvas.toDataURL('image/png');
      const paymentInfoCardWidth = 170; // Full width within margins
      const paymentInfoCardHeight = (200 * paymentInfoCardWidth) / 800; // Proportional height

      pdf.addImage(paymentInfoCardImgData, 'PNG', 20, yPosition, paymentInfoCardWidth, paymentInfoCardHeight);
      yPosition += paymentInfoCardHeight;

      pdf.setFontSize(14);
      pdf.setTextColor(71, 114, 241);
      pdf.setFont("helvetica", "bold");
      pdf.text('Tenant Payment Status', 20, yPosition);
      yPosition += 10;

      let paymentChartHeight = 0;
      if (paymentChartRef.current) {
        const canvas = await html2canvas(paymentChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 80;
        paymentChartHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, paymentChartHeight);
      }

      if (paymentLegendRef.current){
        const legendCanvas = await html2canvas(paymentLegendRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 80;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 100, yPosition + 20, legendWidth, legendHeight);
      }

      yPosition += paymentChartHeight + 10;

      pdf.addPage();
      yPosition = 25;

      //Forecast Chart Section
      pdf.setFontSize(14);
      pdf.setTextColor(71, 114, 241);
      pdf.setFont("helvetica", "bold");
      pdf.text('Monthly Trend Line', 20, yPosition);
      yPosition += 10;

      if (trendChartRef.current) {
        const canvas = await html2canvas(trendChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 80;
        const forecastChartHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, forecastChartHeight);
      }

      if (trendLegendRef.current){
        const legendCanvas = await html2canvas(trendLegendRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 80;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 100, yPosition, legendWidth, legendHeight);
      }

      // Add header and footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);

        const footerY = pageHeight - 15;

        // header section
        pdf.setFontSize(10);
        pdf.setTextColor(71, 114, 241); // Blue color for brand
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', xPosition, 10);
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('Real Estate Management Software', xPosition, 14);
        
        pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);

        // Footer section
        pdf.setFontSize(8);
        pdf.setTextColor(71, 114, 241);
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, footerY + 3);
        
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('|', 42, footerY + 3);
        pdf.text('www.tenafix.co', 44, footerY + 3);
        pdf.text('|', 64, footerY + 3);
        pdf.text('support@tenafix.co', 66, footerY + 3);
        
        // System generated note
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text('This is a system-generated report.', 20, footerY + 6);
        pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
        
        // Page number (right aligned)
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${i} of ${pageCount}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
      }

      // Save PDF
      const localNow = new Date().toLocaleString().split(', ');
      const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
      pdf.save(`payment-distribution-report-${currentDate}.pdf`);

    } catch (error) {
      showToast("error", "Failed to generate PDF. Please try again.");
      console.error("Error generating PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  }
  
  const handleDownload = () => {
    setIsGeneratingPdf(true);
    setTimeout(() => downloadPdf(), 0);
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-[30px]">
      <div className="">
        <TableHeader
          searchValue={filters.propertyId}
          onSearchSubmit={handleSearch}
          filters={[
            {
              name: "Filter by Property",
              value: filters.selectedPropertyName,
              onChange: (val) =>
                setFilters((prev) => ({ ...prev, propertyId: val })),
              options: [
                ...(uniqueProperties.length > 0 &&
                  !uniqueProperties[0].disabled
                    ? [{ label: "All properties", value: "" }]
                    : []),
                ...uniqueProperties,
              ],
            },
            {
              name: "Filter by Month",
              value: filters.month,
              onChange: (val) =>
                setFilters((prev) => ({ ...prev, month: val })),
              options: monthArray.map((month) => ({
                label: month,
                value: month,
              })),
            }
          ]}
          buttons={[
            {
              label: isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "SEARCH"
              ),
              onClick: handleSearch,
              disabled: isLoading,
              variant: "main",
              className:
                "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase w-[102px] flex items-center justify-center",
            },
          {
            label: "DOWNLOAD REPORT",
            onClick: handleDownload,
            loading: isGeneratingPdf,
            disabled: isGeneratingPdf || isLoading,
            className:
              "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[170px] uppercase font-bold",
            },
          ]}
          datePickerClass="sm:w-full md:w-full lg:w-full flex flex-grow w-full"
          buttonWrapperClass="sm:mr-0 mx-auto w-full flex flex-row [@media(min-width:360px)]:w-full   [@media(min-width:360px)]:flex [@media(min-width:360px)]:flex-row justify-center items-center [@media(min-width:640px)]:justify-start [@media(min-width:768px)]:justify-end lg:justify-end"
          containerClass="md:grid lg:grid-cols-3 md:grid-cols-3 flex flex-wrap lg:flex-nowrap xl:grid xl:grid-rows-1"  //[@media(min-width:1516px)]:
          dropdownWrapperClass="sm:max-w-full md:max-w-full flex flex-grow w-full"
        />
      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
            {paymentBreakdown.map((item) => (
            <InfoCard
                key={item.label}
              label={`${item.label} (UGX)`}
              value={item.value}
              className=" max-h-[120px] mx-auto w-full"
              labelStyle="font-inter font-semibold text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[16px] sm:text-[20px] md:text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
            ))}
        </div>
      </div>

        {/* Rent Collected vs Expected */}
        <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4 xl:gap-[60px] mt-[30px]">
          <div>
            <h2 className="text-[22px] font-semibold text-[#4772F1] leading-[100%] font-inter mb-4">
              Tenant Payment Status
            </h2>
            <div 
              ref={paymentChartRef}
              className="rounded-[5px]  border-[2px] border-[#F9F9F9] p-2 xl:p-[30px]   shadow-box-9696">
              <ReactApexChart
                options={rentChartOptions}
                series={rentChartSeries}
                type="bar"
                height={400}
              />
            </div>
          </div>

          <div 
            ref={paymentLegendRef}
            className=" md:relative top-28 rounded-[5px] h-[260px] border-[2px] border-[#F9F9F9] p-2 xl:p-[30px]   shadow-box-9696">
            <LegendTable
              data={paymentBreakdown}
              showDot={true}
              className="mt-4"
              classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
              classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
              classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            />
          </div>
        </div>
        <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
          <h2 className="text-[22px] font-semibold text-[#4772F1] leading-[100%] font-inter mb-4">
            Monthly Trend Line
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4 xl:gap-[60px]">
            <div 
              ref={trendChartRef}
              className="rounded-[5px] xl:w-full border-[2px] border-[#F9F9F9] xl:p-[30px]   shadow-box-9696">
              <ReactApexChart
                options={monthlyTrendChartOptions}
                series={monthlyTrendChartSeries}
                type="area"
                height={400}
              />
            </div>
            <div 
              ref={trendLegendRef}
              className="rounded-[5px] xl:w-full h-[320px] border-[2px] border-[#F9F9F9] p-2 xl:p-[30px]   shadow-box-9696">
              <LegendTable
                data={monthlyTrendData.map((item) => {
              const total = monthlyTrendData.reduce(
                (sum, d) => sum + parseFloat(d.value || 0),
                0
              );
                  const value = parseFloat(item.value);
                  const percent =
                    total > 0 ? (value / total) * 100 : 0;
                  return {
                    label: item.label,
                    value: value.toFixed(2),
                    percent: percent,
                  };
                })}
                showDot={false}
                className="mt-4"
                classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
                classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
                classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDistribution;
